import apollo from '@/graphql'

import cache from '@/store/helpers/cache.helper'

import LastImpressionMetricsQuery from '@/graphql/metrics/LastImpressionMetricsQuery.gql'

function initialState() {
  return {
    isLoading: false,
    metrics: {},
    filters: {
      campaignsIds: [],
      retailersIds: [],
      storesIds: [],
      startDate: Date.now() - (14 * 24 * 3600000),
      endDate: Date.now(),
    },
  }
}

const state = initialState()

// getters
const getters = {
  filterDates(state) {
    return {
      startDate: state.filters.startDate,
      endDate: state.filters.endDate,
    }
  },
  impressionsPlaysChartData(state) {
    let mutableTrends = state.metrics.trends

    if (!mutableTrends || mutableTrends.length === 0) {
      return {
        labels: [],
        datasets: [],
      }
    }

    const d1 = new Date()
    const d2 = new Date(mutableTrends[mutableTrends.length - 1].day)

    const printDate = d => `${d.getFullYear()}.${d.getMonth()}.${d.getDate()}`

    if (printDate(d1) === printDate(d2)) {
      // remove the last data point
      mutableTrends = mutableTrends.slice(0, -1)
    }

    return {
      labels: mutableTrends.map(
        dataPoint => dataPoint.day,
      ),
      datasets: [
        {
          label: 'Plays',
          backgroundColor: '#ffffff',
          borderColor: '#246bd8',
          pointBackgroundColor: '#246bd8',
          fill: false,
          lineTension: 0,
          pointRadius: 3,
          data: mutableTrends.map(
            dataPoint => dataPoint.plays,
          ),
        },
        {
          label: 'Impressions',
          backgroundColor: '#ffffff',
          borderColor: '#03c7a4',
          fill: false,
          pointBackgroundColor: '#03c7a4',
          lineTension: 0,
          pointRadius: 3,
          data: mutableTrends.map(
            dataPoint => dataPoint.impressions,
          ),
        },
      ],
    }
  },
}

// mutations
const mutations = {
  reset(state) {
    const s = initialState()
    Object.keys(s).forEach((key) => {
      state[key] = s[key]
    })
    cache.remove('adMetrics')
  },

  initializeStore() {
    state.metrics = cache.get('adMetrics') || {}
  },

  setMetrics(state, metrics) {
    state.metrics = metrics
  },

  setFilter(state, filter) {
    Object.keys(filter).forEach((key) => {
      state.filters[key] = filter[key]
    })
  },
}

// actions
const actions = {
  init({ commit }) {
    commit('initializeStore')
  },

  async load({ commit, state }, args) {
    if (!cache.get('adMetrics')) {
      state.isLoading = true
    }

    // Filters are passed in from load dispatcher
    if (args !== undefined && args.filter) {
      commit('setFilter', args.filter)
    }

    const filterCopy = Object.assign({}, state.filters)

    if (state.filters) {
      // Normalize to the nearest day
      // Allows Apollo to cache the result of same-day filter queries
      filterCopy.startDate = Math.floor(filterCopy.startDate / 36e5) * 36e5
      filterCopy.endDate = Math.floor(filterCopy.endDate / 36e5) * 36e5
    }

    const hasFilter = filterCopy.campaignsIds.length
      || filterCopy.retailersIds.length
      || filterCopy.storesIds.length
      || filterCopy.startDate !== Math.floor((Date.now() / 3.6e6) - (14 * 24)) * 3.6e6
      || filterCopy.endDate !== Math.floor(Date.now() / 3.6e6) * 3.6e6

    // Making filter date values into strings because that is what our resolver expects
    filterCopy.startDate = String(filterCopy.startDate)
    filterCopy.endDate = String(filterCopy.endDate)

    /**
     * 1. Only cache the unfiltered (aka default) metrics
     * 2. Show loading unless initial load of default metrics
     */
    if (hasFilter) {
      state.isLoading = true
    }

    const { data: { lastImpressionMetrics } } = await apollo.defaultClient.query({
      // fetchPolicy: 'network-only',
      // Don't use pollInterval here, because it will requery for each
      // combination of filters that is used.
      query: LastImpressionMetricsQuery,
      variables: { filter: filterCopy },
    })

    state.isLoading = false

    if (!hasFilter) {
      cache.set('adMetrics', lastImpressionMetrics)
    }

    commit('setMetrics', lastImpressionMetrics)
  },

  reset({ commit }) {
    commit('reset')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/metrics/AdMetricsChart.js